import config from '../config';

const SESSION_KEY = 'golf.renewableSession';
const LOGOUT_KEY = 'golf.logoutPending';
const LOGOUT_TOKEN_KEY = 'golf.logoutPendingToken';
const EPOCH_KEY = 'golf.authEpoch';
export const AUTH_EVENT = 'golf-auth-changed';
let generation = 0;
let refreshPromise = null;
let authQueue = Promise.resolve();
function withAuthLock(work) {
  if (navigator.locks?.request) return navigator.locks.request('golf-session-refresh', work);
  const next = authQueue.catch(() => {}).then(work);
  authQueue = next;
  return next;
}
const cookieMode = () => config.apiUrl === window.location.origin ||
  (process.env.NODE_ENV === 'development' && new URL(config.apiUrl).hostname === window.location.hostname);
export const isRenewableSession = () => localStorage.getItem(SESSION_KEY) === 'true';
export const hasPendingLogout = () => localStorage.getItem(LOGOUT_KEY) === 'true';
export function tokenExpiresSoon(token = localStorage.getItem('token')) {
  try {
    const { exp } = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return Number.isFinite(exp) && exp * 1000 < Date.now() + 60000;
  } catch { return false; } // Legacy/non-JWT fixtures are still checked by the server.
}
export function clearAuthentication() {
  generation++;
  try { localStorage.setItem(EPOCH_KEY, `${Date.now()}-${Math.random()}`); } catch { /* Logout must still work when quota is full. */ }
  for (const key of ['token', 'userId', 'user', SESSION_KEY]) localStorage.removeItem(key);
  // Deliberately never clear localScores, liftedBall, preferences or other app data.
  window.dispatchEvent(new Event(AUTH_EVENT));
}
export function saveAuthentication(data) {
  generation++;
  localStorage.setItem(EPOCH_KEY, `${Date.now()}-${Math.random()}`);
  localStorage.setItem('token', data.token);
  localStorage.setItem('userId', data.user.userId);
  localStorage.setItem('user', JSON.stringify(data.user));
  localStorage.setItem(SESSION_KEY, String(!!data.renewable));
  localStorage.removeItem(LOGOUT_KEY);
  window.dispatchEvent(new Event(AUTH_EVENT));
}
function abortIfNeeded(signal) {
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
}
async function sessionRequest(path, body = {}, headers = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 12000);
  try {
    const response = await fetch(`${config.apiUrl}/api/auth/${path}`, {
      method: 'POST', credentials: cookieMode() ? 'include' : 'omit', signal: controller.signal,
      headers: { 'Content-Type': 'application/json', ...headers }, body: JSON.stringify(body)
    });
    // Keep the timeout active until the body arrives too (not only the headers).
    const data = response.status === 204 ? null : await response.json().catch(() => null);
    return { ok: response.ok, status: response.status, json: async () => data };
  } finally { clearTimeout(timer); }
}
export async function loginRequest(path, body) {
  const epoch = localStorage.getItem(EPOCH_KEY);
  return withAuthLock(async () => {
    if (localStorage.getItem(EPOCH_KEY) !== epoch) throw new Error('Session changed');
    // Serialize cookie responses across login, refresh and logout, including other tabs.
    if (hasPendingLogout()) await revokePendingLogout();
    const response = await sessionRequest(path, { ...body, ...(cookieMode() ? { sessionMode: 'cookie' } : {}) });
    if (localStorage.getItem(EPOCH_KEY) !== epoch) throw new Error('Session changed');
    if (response.ok) {
      const data = await response.json();
      if (!data?.token || !data.user?.userId) throw new Error('Invalid login response');
      saveAuthentication(data);
    }
    return response;
  });
}
export async function refreshSession(failedToken) {
  if (!isRenewableSession() || hasPendingLogout()) return null;
  if (refreshPromise) return refreshPromise;
  const startedGeneration = generation;
  const epoch = localStorage.getItem(EPOCH_KEY);
  const userId = localStorage.getItem('userId');
  const originalToken = failedToken || localStorage.getItem('token');
  const isCurrent = () => generation === startedGeneration && localStorage.getItem(EPOCH_KEY) === epoch &&
    localStorage.getItem('userId') === userId && !hasPendingLogout();
  const run = async () => {
    if (!isCurrent()) return null;
    const current = localStorage.getItem('token');
    if (current !== originalToken && !tokenExpiresSoon(current)) return current;
    const response = await sessionRequest('refresh');
    if (!isCurrent()) return null;
    if (response.status === 401) { clearAuthentication(); return null; }
    if (!response.ok) throw new Error('Session temporarily unavailable');
    const data = await response.json();
    if (!isCurrent()) return null;
    if (data?.user?.userId !== userId || !data.token) throw new Error('Session identity changed');
    localStorage.setItem('token', data.token);
    return data.token;
  };
  const work = withAuthLock(run);
  refreshPromise = work;
  try { return await work; } finally { if (refreshPromise === work) refreshPromise = null; }
}

export async function apiFetch(url, options = {}) {
  const target = new URL(url, window.location.origin);
  if (target.origin !== new URL(config.apiUrl).origin || !target.pathname.startsWith('/api/')) {
    throw new Error('Refusing to send API credentials to another origin');
  }
  const headers = { ...options.headers };
  const authenticated = !!headers.Authorization || !!headers.authorization;
  delete headers.authorization;
  const userId = localStorage.getItem('userId');
  const startedGeneration = generation;
  const epoch = localStorage.getItem(EPOCH_KEY);
  const sameIdentity = () => generation === startedGeneration && localStorage.getItem(EPOCH_KEY) === epoch && localStorage.getItem('userId') === userId;
  if (authenticated && isRenewableSession() && tokenExpiresSoon()) await refreshSession();
  abortIfNeeded(options.signal);
  if (authenticated && (hasPendingLogout() || !sameIdentity())) {
    throw new Error('Session changed');
  }
  const send = () => {
    abortIfNeeded(options.signal);
    if (authenticated) headers.Authorization = `Bearer ${localStorage.getItem('token') || ''}`;
    return fetch(url, { ...options, headers: { ...headers }, credentials: 'omit' });
  };
  const sentToken = localStorage.getItem('token');
  let response = await send();
  if (authenticated && !sameIdentity()) throw new Error('Session changed');
  if (authenticated && response.status === 401 && sameIdentity()) {
    // Retry ONLY a rejected authentication. Never retry timeouts/5xx or unknown write outcomes.
    if (isRenewableSession() && await refreshSession(sentToken)) {
      abortIfNeeded(options.signal);
      if (!sameIdentity()) throw new Error('Session changed');
      response = await send();
      if (!sameIdentity()) throw new Error('Session changed');
    }
    if (response.status === 401 && sameIdentity()) clearAuthentication();
  }
  if (authenticated && response.ok && typeof response.json === 'function') {
    const readJson = response.json.bind(response);
    response.json = async () => {
      const data = await readJson();
      if (!sameIdentity()) throw new Error('Session changed');
      return data;
    };
  }
  return response;
}

async function revokePendingLogout() {
  if (!hasPendingLogout()) return;
  const token = localStorage.getItem(LOGOUT_TOKEN_KEY);
  const response = await sessionRequest('logout', {}, token ? { Authorization: `Bearer ${token}` } : {});
  if (!response.ok) throw new Error('Logout pending');
  localStorage.removeItem(LOGOUT_KEY);
  localStorage.removeItem(LOGOUT_TOKEN_KEY);
}
export const finishPendingLogout = () => withAuthLock(revokePendingLogout);
export async function logoutSession() {
  // Keep the HttpOnly cookie for a later revocation if offline, but never auto-login with it.
  const token = localStorage.getItem('token');
  clearAuthentication();
  // Removing the profile/active credentials first also frees storage for this marker.
  if (token) localStorage.setItem(LOGOUT_TOKEN_KEY, token);
  localStorage.setItem(LOGOUT_KEY, 'true');
  try {
    await finishPendingLogout();
    return true;
  } catch { return false; }
}
